import { useMemo, useState, type FormEvent } from 'react'
import { Building2, UserPlus, X } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import Select from '@/components/ui/Select'
import SearchableSelect from '@/components/ui/SearchableSelect'
import { useCreateDeal, usePipelineDetail, usePipelines } from '@/features/board/useBoard'
import { useContactSearch, useContacts, useCreateContact } from '@/features/contacts/useContacts'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { Contact, Deal } from '@/api/types'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

const nameOf = (c: Pick<Contact, 'firstName' | 'lastName' | 'email'>) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed contact'

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

/**
 * "New deal" in one dialog: the property, who it's for (an existing client, or a new one typed in on
 * the spot), the stage and the value — the deal is created with its client already linked and opens
 * straight away. Used from the board's main button and from a contact ("New deal for Sarah").
 */
export default function NewDealModal({
  pipelineId: initialPipelineId,
  stageId: initialStageId,
  contact,
  onClose,
  onCreated,
}: {
  /** The board's current pipeline; from a contact, the default pipeline is used. */
  pipelineId?: string
  /** Pre-picked stage (a column's "+"); otherwise the pipeline's first stage. */
  stageId?: string
  /** Pre-picked client (opened from a contact). */
  contact?: Contact
  onClose: () => void
  onCreated: (deal: Deal) => void
}) {
  const { data: pipelines } = usePipelines()
  const [pipelineId, setPipelineId] = useState(initialPipelineId ?? '')
  const effectivePipelineId = pipelineId || pipelines?.find((p) => p.isDefault)?.id || pipelines?.[0]?.id || ''
  const { data: pipeline } = usePipelineDetail(effectivePipelineId || undefined)
  const stageGroups = useMemo(
    () =>
      pipeline
        ? [
            ...pipeline.sections.map((s) => ({ label: s.name, stages: s.stages })),
            { label: 'Other stages', stages: pipeline.looseStages },
          ].filter((g) => g.stages.length > 0)
        : [],
    [pipeline],
  )
  const firstStageId = stageGroups[0]?.stages[0]?.id ?? ''
  const [stageId, setStageId] = useState(initialStageId ?? '')
  // A pipeline switch makes the old stage meaningless — fall back to the new pipeline's first stage.
  const effectiveStageId = stageGroups.some((g) => g.stages.some((s) => s.id === stageId)) ? stageId : firstStageId

  const [title, setTitle] = useState('')
  const [value, setValue] = useState('')

  // Client: pick an existing contact (server search) or type a new one.
  const [clientId, setClientId] = useState(contact?.id ?? '')
  const [picked, setPicked] = useState<{ value: string; label: string } | null>(
    contact ? { value: contact.id, label: nameOf(contact) } : null,
  )
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query.trim(), 300)
  const { data: firstPage } = useContacts()
  const { data: searchPage } = useContactSearch(debounced)
  const options = useMemo(() => {
    const items = (debounced ? searchPage : firstPage)?.items ?? []
    const opts = items.map((c) => ({
      value: c.id,
      label: [nameOf(c), c.mobile || c.phone].filter(Boolean).join(' · '),
    }))
    if (picked && !opts.some((o) => o.value === picked.value)) opts.unshift(picked)
    return opts
  }, [debounced, searchPage, firstPage, picked])
  const [newClient, setNewClient] = useState<{ firstName: string; lastName: string; email: string; mobile: string } | null>(null)

  const create = useCreateDeal(effectivePipelineId)
  const createContact = useCreateContact()
  const [error, setError] = useState<string | null>(null)
  const busy = create.isPending || createContact.isPending
  const newClientOk = !newClient || !!(newClient.firstName.trim() || newClient.lastName.trim() || newClient.email.trim())
  const canSave = !!title.trim() && !!effectivePipelineId && !!effectiveStageId && newClientOk && !busy

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSave) return
    setError(null)
    try {
      let primaryContactId: string | null = clientId || null
      if (newClient) {
        const { contact: created } = await createContact.mutateAsync({
          firstName: newClient.firstName.trim() || undefined,
          lastName: newClient.lastName.trim() || undefined,
          email: newClient.email.trim() || undefined,
          mobile: newClient.mobile.trim() || undefined,
        })
        primaryContactId = created.id
      }
      const deal = await create.mutateAsync({
        title: title.trim(),
        pipelineId: effectivePipelineId,
        stageId: effectiveStageId,
        value: value.trim() === '' ? null : Number(value.replace(/[^\d.]/g, '')),
        primaryContactId,
      })
      onCreated(deal)
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  return (
    <Modal
      title={contact ? `New deal for ${nameOf(contact)}` : 'New deal'}
      icon={<Building2 size={17} className="text-brand-primary" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
          <button type="submit" form="new-deal-form" disabled={!canSave} className={modalButton.primary}>
            {busy ? 'Creating…' : 'Create deal'}
          </button>
        </>
      }
    >
      <form id="new-deal-form" onSubmit={submit} className="space-y-3">
        <Field label="Property or deal name">
          <input autoFocus className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 27 Park Lane, Ashgrove" />
        </Field>

        <Field label="Client">
          {newClient ? (
            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-brand-dark">New client</span>
                <button type="button" onClick={() => setNewClient(null)} className="text-xs text-gray-500 hover:text-brand-dark inline-flex items-center gap-1">
                  <X size={12} /> Pick an existing client instead
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input aria-label="First name" placeholder="First name" className={inputCls} value={newClient.firstName} onChange={(e) => setNewClient({ ...newClient, firstName: e.target.value })} />
                <input aria-label="Last name" placeholder="Last name" className={inputCls} value={newClient.lastName} onChange={(e) => setNewClient({ ...newClient, lastName: e.target.value })} />
                <input aria-label="Mobile" placeholder="Mobile" className={inputCls} value={newClient.mobile} onChange={(e) => setNewClient({ ...newClient, mobile: e.target.value })} />
                <input aria-label="Email" placeholder="Email" className={inputCls} value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <SearchableSelect
                size="md"
                value={clientId}
                onChange={(id) => {
                  setClientId(id)
                  setPicked(options.find((o) => o.value === id) ?? null)
                }}
                onSearchChange={setQuery}
                placeholder="Search by name, phone or email…"
                searchPlaceholder="Type a name, phone or email"
                options={options}
                className="flex-1 min-w-0"
              />
              <button
                type="button"
                onClick={() => {
                  setNewClient({ firstName: '', lastName: '', email: '', mobile: '' })
                  setClientId('')
                  setPicked(null)
                }}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
              >
                <UserPlus size={14} /> New client
              </button>
            </div>
          )}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Stage">
            <Select size="md" value={effectiveStageId} onChange={(e) => setStageId(e.target.value)} className="w-full">
              {stageGroups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </optgroup>
              ))}
            </Select>
          </Field>
          <Field label="Value (optional)">
            <input className={inputCls} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 950,000" />
          </Field>
        </div>

        {(pipelines?.length ?? 0) > 1 && (
          <Field label="Pipeline">
            <Select size="md" value={effectivePipelineId} onChange={(e) => setPipelineId(e.target.value)} className="w-full">
              {(pipelines ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}

        <p className="text-[11px] text-gray-400">The deal is yours; you can hand it to a colleague from the deal afterwards.</p>
        {error && <p className="text-danger text-xs">{error}</p>}
      </form>
    </Modal>
  )
}
